<template>
  <section class="vacancy-main">
    <div v-if="vacancy.is_hidden" class="vacancy-main__hidden-vacancy-alert">
      <BaseSvg name="ico-strikethrough-circle" width="1.5rem" height="1.5rem" />
      <span>{{ $tVacancy('This vacancy is no longer relevant') }}</span>
      <router-link-modified
        class="base-btn base-btn--blue base-btn--x-small ml-auto"
        :to="hiddenVacancyRelevantsRoute"
      >
        {{ $tVacancy('See similar vacancies') }}
      </router-link-modified>
    </div>

    <div v-if="!currentLocaleSameAsVacancyOriginal" class="vacancy-main__translate-header">
      <div>{{ translateAlertText }}</div>
      <button
        class="base-btn base-btn--x-small base-btn--outline-blue"
        @click="reloadWithNewLocale"
      >
        {{ translateButtonText }}
      </button>
    </div>

    <div class="vacancy-main__id mt-3 mb-1">
      <span>ID: {{ vacancy.ID }}</span>
    </div>
    <div class="vacancy-main__title-wrap">
      <h1 class="vacancy-main__title">{{ vacancy[1].name }}</h1>
      <VacancyMainBookmarks v-if="canRespondOnVacancy"
                            :inBookmarks="vacancy.in_bookmarks"
                            :pending="pendingBookmarks"
                            @toggle="$emit('toggle-bookmarks')"/>
    </div>
    <div class="vacancy-main__label-wrap">
      <div v-if="vacancy.is_hot"
           class="vacancy-main__label  _hot">
        <BaseSvg name="ico-flame"
                 width="1.6rem"
                 height="2rem"/>
        <span v-if="vacancy[1].work_start_text">{{ vacancy[1].work_start_text }}</span>
      </div>
      <div v-else
           class="vacancy-main__label">
        <BaseSvg name="ico-clock"
                 width="1.8rem"
                 height="1.8rem"/>
        <span v-if="vacancy[1].work_start_text">{{ vacancy[1].work_start_text }}</span>
      </div>
    </div>
    <VacancyMediaCarousel v-if="isMob"
                          :slides="vacancy[3].images"
                          @gallery="$emit('gallery', $event)"
                          class="vacancy-main__media-carousel"/>

    <VacancySalary :vacancy="vacancy"
                   class="vacancy-main__salary"/>
    <div class="vacancy-main__location-row">
      <VacancyLocation :vacancy="vacancy"
                       class="vacancy-main__location"/>

      <div class="vacancy-main__created  d-sm-none">
        <BaseSvg name="ico-plus"/>
        <span class="vacancy-main__created-title">{{ $tVacancy('Updated:') }}</span>
        <span :class="{_yellow: createdMoreThenMonth}"
              class="vacancy-main__created-value">{{ createdFormat }}</span>
      </div>

      <div class="vacancy-main__viewed">
        <BaseSvg name="ico-see"/>
        {{ vacancy.views }}
      </div>
    </div>

    <div class="vacancy-main__created  d-none  d-sm-flex">
      <span class="vacancy-main__created-title">{{ $tVacancy('Updated:') }}</span>
      <span :class="{_yellow: createdMoreThenMonth}"
            class="vacancy-main__created-value">{{ createdFormat }}</span>
    </div>

    <VacancyMainRespondBtn
      v-if="canRespondOnVacancy && !vacancy.is_hidden && smOrDown"
      :vacancy="vacancy"
      style="animation: none"
      class="vacancy-btns__btn _respond _wide-uppercase mb-5 mr-0"
    />

    <div
      class="vacancy-main__features"
      :class="{ _compressed: compressedTags }"
      @click="showAllTags = !showAllTags"
    >
      {{ vacancy.features.join(' • ') }}
    </div>

    <VacancyMainItems :vacancy="vacancy"
                      :isMob="isMob"/>

    <div v-if="canRespondOnVacancy && !vacancy.is_hidden"
         class="vacancy-main__main-btns  vacancy-btns">
      <VacancyMainBookmarksBtn :inBookmarks="vacancy.in_bookmarks"
                               :pending="pendingBookmarks"
                               @toggle="$emit('toggle-bookmarks')"
                               class="vacancy-btns__btn  _bookmarks"/>
      <VacancyMainRespondBtn :vacancy="vacancy"
                             class="vacancy-btns__btn  _respond"/>
    </div>
    <VacancyActual v-if="!isDisabled"
                   :vacancy="vacancy"
                   class="vacancy-main__main-actual"/>
  </section>
</template>

<script>
  import windowSizeMixin from '../../mixins/windowSizeMixin';
  import getDaysBetween from '@/helpers/getDaysBetween'
  import parseDate from '@/helpers/parseDate'
  import VacancyMainBookmarks from './VacancyMainBookmarksShortBtn'
  import VacancyMainBookmarksBtn from './VacancyMainBookmarksBtn'
  import VacancyMainRespondBtn from './VacancyMainRespondBtn'
  import VacancyMainItems from './VacancyMainItems'
  import VacancySalary from './VacancySalary'
  import VacancyLocation from './VacancyLocation'
  import VacancyActual from './VacancyActual'
  import VacancyMediaCarousel from './VacancyMediaCarousel'

  export default {
    name: 'VacancyMain',
    components: {
      VacancyMainBookmarks,
      VacancyMainBookmarksBtn,
      VacancyMainRespondBtn,
      VacancyMainItems,
      VacancySalary,
      VacancyLocation,
      VacancyActual,
      VacancyMediaCarousel,
    },
    mixins: [windowSizeMixin],
    props: [
      'vacancy',
      'isDisabled',
      'canRespondOnVacancy',
      'pendingBookmarks',
      'isMob'
    ],
    data() {
      return {
        showAllTags: false,
      };
    },
    computed: {
      created() {
        return getDaysBetween(parseDate(this.vacancy.updated_at), new Date())
      },
      createdMoreThenMonth() {
        return this.created > 30
      },
      createdFormat() {
        // "Сегодня"/"Вчера"/"2/3/4...29/30) дня(-ей) назад"/"Более месяца назад"
        const created = this.created

        if(created < 1) {
          return this.$tVacancy('Today')
        }

        if(created === 1) {
          return this.$tVacancy('Yesterday')
        }

        if (this.createdMoreThenMonth) {
          return this.$tVacancy('More than a month ago')
        }

        return created + ' ' + this.$tVacancy('days ago', created)
      },
      currentLocaleSameAsVacancyOriginal() {
        return this.$store.state.currentLocale === this.vacancy.original_locale;
      },
      isOriginalLocale() {
        return this.vacancy.locale === this.vacancy.original_locale;
      },
      translateAlertText() {
        return this.isOriginalLocaleformSalaryPlaceholder
          ? this.$tVacancy('Translate into system lang')
          : this.$tVacancy('Translated into system lang with Google');
      },
      translateButtonText() {
        return this.isOriginalLocale ? this.$tVacancy('Translate') : this.$tVacancy('Show original');
      },
      hiddenVacancyRelevantsRoute() {
        function isArrayEmpty(arr) {
          return !arr || !arr.length
        }

        const personsSlugs = this.vacancy[2].person_types?.map(t => t?.slug_for_filter);
        const workPlaceCountryId = this.vacancy[3].work_place_country?.id;
        const staticFilters = [];
        let routeName = '';

        if (workPlaceCountryId) {
          staticFilters.push(`country-${workPlaceCountryId}`);
        }

        if (isArrayEmpty(staticFilters) && isArrayEmpty(personsSlugs)) {
          routeName = 'vacancies'
        } else if (isArrayEmpty(staticFilters)) {
          routeName = 'vacancies-with-dynamic'
        } else if (isArrayEmpty(personsSlugs)) {
          routeName = 'vacancies-with-static'
        } else {
          routeName = 'vacancies-with-static&dynamic'
        }

        return {
          name: routeName,
          params: {
            dynamicFilters: personsSlugs.join('-'),
            staticFilters,
          },
        };
      },
      compressedTags() {
        if (!this.smOrDown) return false;
        return !this.showAllTags;
      },
    },
    mounted() {},
    methods: {
      reloadWithNewLocale() {
        const options = { headers: { 'X-App-Locale': this.vacancy.original_locale } };
        const conditionalOptions = this.isOriginalLocale ? null : options;
        this.$emit('reload', conditionalOptions);
      },
    },
  }
</script>

<style lang="less">

</style>
